import { AxiosError } from 'axios';
import { getDeploymentConfig } from '../../services/config';
import { getDynamicSetting } from '../../utils/dynamicSettings';
import { isRedirectLoop, isValidRedirectUrl } from '../../utils/url';
import { RedirectValidationResult } from './types';

/**
 * The canonical Qodo domain. Cross-Qodo tenant redirects (central `app.qodo.ai`
 * ↔ a dedicated-org portal like `ford.st.qodo.ai`) target hosts under this
 * domain but sit outside a tenant-scoped `parent_domain`, so they are trusted
 * explicitly rather than via the deployment allowlist.
 */
const CENTRAL_QODO_DOMAIN = 'qodo.ai';

/**
 * True for the `qodo.ai` apex or any of its subdomains. The `.` prefix on the
 * suffix check enforces a real subdomain boundary, so lookalikes such as
 * `evilqodo.ai` are rejected.
 */
const isCentralQodoHost = (hostname: string): boolean =>
  hostname === CENTRAL_QODO_DOMAIN ||
  hostname.endsWith(`.${CENTRAL_QODO_DOMAIN}`);

/**
 * Determines whether a redirect target host is within the deployment's trust
 * boundary. The allowlist is derived strictly from configured values — never
 * from a hostname-stripping heuristic, which could widen the boundary to a
 * domain broader than intended when `parent_domain` is absent.
 *
 * Allowed when the target host is:
 * - any `*.qodo.ai` host (central ↔ tenant Qodo redirects), or
 * - the backend `server_url` host, or
 * - the current page host (same-host redirect), or
 * - equal to / a subdomain of `parent_domain` (only when configured).
 */
const isAllowedRedirectDomain = (url: string): boolean => {
  try {
    const { hostname } = new URL(url);

    if (isCentralQodoHost(hostname)) {
      return true;
    }

    const config = getDeploymentConfig();
    const parentDomain = config.parent_domain;
    const serverHostname = new URL(config.server_url).hostname;
    const currentHostname = window.location.hostname;

    if (hostname === serverHostname || hostname === currentHostname) {
      return true;
    }

    if (parentDomain) {
      return hostname === parentDomain || hostname.endsWith(`.${parentDomain}`);
    }

    return false;
  } catch {
    return false;
  }
};

/**
 * Resolves and validates a redirect URL from a 409 error response.
 * Extracts the redirect URL from FastAPI's detail wrapper and validates it against:
 * - Type checking (must be a string)
 * - URL format validation
 * - Redirect loop detection
 *
 * @param error - The Axios error response containing potential redirect information
 * @returns Validation result with either the valid redirect URL or a reason for rejection
 *
 * @example
 * const result = resolveAndValidateRedirect(error);
 * if (result.valid) {
 *   window.location.href = result.redirectTo;
 * } else {
 *   console.error(result.reason);
 * }
 */
export const resolveAndValidateRedirect = (
  error: AxiosError,
): RedirectValidationResult => {
  const responseData = error.response?.data as {
    detail?: { redirectTo?: string };
  };
  // FastAPI wraps HTTPException detail in a "detail" field
  const redirectTo = responseData?.detail?.redirectTo;

  // Validate redirectTo exists and is a string
  if (!redirectTo || typeof redirectTo !== 'string') {
    return {
      valid: false,
      reason: `409 response missing or invalid redirectTo field: ${JSON.stringify(
        responseData?.detail,
      )}`,
    };
  }

  // Validate URL format
  if (!isValidRedirectUrl(redirectTo)) {
    return {
      valid: false,
      reason: `Invalid redirect URL in 409 response: ${redirectTo}`,
    };
  }

  // Validate that the redirect target belongs to a known-safe domain
  if (!isAllowedRedirectDomain(redirectTo)) {
    return {
      valid: false,
      reason: `Redirect URL domain not in allowed list: ${redirectTo}`,
    };
  }

  // Check for redirect loops
  if (isRedirectLoop(redirectTo)) {
    return {
      valid: false,
      reason: `Redirect loop detected for URL: ${redirectTo}`,
    };
  }

  return { valid: true, redirectTo: redirectTo };
};

export const getServerUrl = () => getDynamicSetting('server_url');
